import { formatUnits, parseUnits } from '@ethersproject/units';
import React, { useMemo, useState, useContext } from 'react';
import {
    SupportedAssets,
    SupportedChains,
    WITHDRAWAL_AMOUNT_OVERFLOW,
    WITHDRAWAL_COMPLETED,
    WITHDRAWAL_FAST,
    WITHDRAWAL_SLOW,
    WithdrawlTypes,
} from '../../config/constants';
import modalContext from '../../context/modal/modalContext';
import Button from '../../components/Button';
import CustomModal from '../../components/CustomModal';
import TextInput from '../../components/TextInput';
import appContext from '../../context/app/appContext';
import useUserBalance from '../../hooks/web3/useUserBalance';
import { formatAccount, formatUSD } from '../../helpers/formats';
import { useWeb3React } from '@web3-react/core';
import { AddressZero } from '@ethersproject/constants';
import { preventOverflow, sleep, toBigNumber } from '../../web3/utils';
import ASSETS from '../../config/constants/assets.json';
import ARBITRUM_ASSETS from '../../config/constants/arbitrumAssets.json';
import { BigNumber } from 'ethers';
import errorContext from '../../context/error/errorContext';
import { ArrowDownIcon, AssetIcons } from '../../assets/icons';
import useUserSpools from '../../hooks/web3/vault/useUserSpools';
import CustomModalHeader from '../../components/CustomModal/CustomModalHeader';

const SpoolWithdraw: React.FC = () => {
    const { account, chainId } = useWeb3React();
    const { modalData, openModal, closeModal, setModalData } = useContext(modalContext);

    const { sdk, selectedSpool, chainStrategies } = useContext(appContext);

    const { updateUserSpool } = useUserSpools();

    const { setError } = useContext(errorContext);

    const initialWithdrawAddress = useMemo(
        () => ({
            from: '',
            to: '',
        }),
        []
    );

    const [withdrawAddresses, setWithdrawAddresses] = useState(initialWithdrawAddress);

    const userBalance = useUserBalance(selectedSpool?.asset.id);

    const onClose = () => {
        closeModal('spoolWithdraw');
        setWithdrawAddresses(initialWithdrawAddress);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setWithdrawAddresses((prev) => ({
            ...prev,
            [name]: preventOverflow(value, selectedSpool?.asset.decimals),
        }));
    };

    const handleMaxInput = () => {
        setWithdrawAddresses((prev) => ({
            ...prev,
            from: selectedSpool?.userData?.userCurrentBalance.toString() || '0',
        }));
    };

    const withdraw = async () => {
        if (!sdk || !selectedSpool) return;

        const decimals = selectedSpool?.asset.decimals;

        if (
            toBigNumber(
                selectedSpool.userData!.userCurrentBalance.toString(),
                decimals
            ).lt(toBigNumber(withdrawAddresses.from, decimals))
        ) {
            openModal('errorModal');
            setModalData((prev) => ({
                ...prev,
                message: WITHDRAWAL_AMOUNT_OVERFLOW,
            }));
            return;
        }

        const withdrawalAmount = toBigNumber(withdrawAddresses.from, decimals);

        const userCurrentBalance = toBigNumber(
            selectedSpool!.userData!.userCurrentBalance.toString(),
            decimals
        );

        const share = withdrawalAmount
            .mul(BigNumber.from(selectedSpool!.userData!.userShares))
            .div(userCurrentBalance)
            .toString();

        modalData.type === WithdrawlTypes.SLOW
            ? slowWithdraw(share, withdrawalAmount.eq(userCurrentBalance))
            : fastWithdraw(share);
    };

    const slowWithdraw = async (share: string, withdrawAll: boolean) => {
        try {
            setModalData((prev) => ({ ...prev, message: WITHDRAWAL_SLOW }));
            openModal('waitingModal');

            const slowWithdrawal = await sdk!.spools.withdrawFromSpool(
                selectedSpool!.address,
                share,
                withdrawAll
            );

            const receipt = await slowWithdrawal.wait();

            // delay so that subgraph gets updated
            await sleep(5000);

            await updateUserSpool(selectedSpool!, 'pendingWithdrawalAmount');
            onClose();
            setModalData((prev) => ({ ...prev, txHash: receipt.transactionHash }));
            openModal('withdrawalPending');
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    const fastWithdraw = async (share: string) => {
        try {
            setModalData((prev) => ({ ...prev, message: WITHDRAWAL_FAST }));
            openModal('waitingModal');

            const slippages = selectedSpool!.strategies.map(
                ({ address }) => chainStrategies[address]?.slippage
            );

            const fastWithdrawal = await sdk!.spools.withdrawFastFromSpool(
                selectedSpool!.address,
                account!,
                share,
                slippages
            );

            const receipt = await fastWithdrawal.wait();

            await sleep(5000);

            await updateUserSpool(selectedSpool!, 'userCurrentBalance');

            onClose();
            setModalData((prev) => ({
                ...prev,
                status: WITHDRAWAL_COMPLETED,
                txHash: receipt.transactionHash,
                message: '',
            }));
            openModal('successModal');
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    return (
        <CustomModal modalName='spoolWithdraw' className='spool-withdraw'>
            <CustomModalHeader
                title={`${modalData?.type} Withdrawal`}
                onClose={onClose}
            />
            <div className={`note ${modalData?.type?.toLowerCase() ?? ''}`}>
                {modalData?.type === WithdrawlTypes.SLOW
                    ? `Your withdrawal will be set to pending, and will become ready to claim the next time Do Hard Work is executed. (Generally within ${
                          chainId === SupportedChains.ARBITRUM_ONE ? '24 hours' : '3 days'
                      })`
                    : `Withdraw immediately, at a higher cost. Your actual withdrawal may vary due to slippage, fees, and much higher gas costs. (May require multiple transactions)`}
            </div>
            <div className='content'>
                <div className='deposit-container deposit-from'>
                    <div className='field-container from'>
                        <span className='field'>From</span>
                        <span>{selectedSpool?.name || 'Spool'}</span>
                    </div>
                    <div className='field-container balance'>
                        <span className='field'>Balance in $SPOOL</span>
                        <span>
                            {selectedSpool?.userData?.userCurrentBalance
                                ? formatUSD(
                                      selectedSpool.userData.userCurrentBalance || 0
                                  )
                                : '0.00'}
                        </span>
                    </div>
                    <div className='field-container input'>
                        <span className='field'>Input</span>
                        <div className='input-container'>
                            <TextInput
                                onChange={handleChange}
                                value={withdrawAddresses.from}
                                name='from'
                                placeholder='0.00'
                            />
                            <Button variant='secondary' onClick={handleMaxInput}>
                                MAX
                            </Button>
                        </div>
                    </div>

                    <div className='field-container token'>
                        <div>
                            {
                                AssetIcons[
                                    selectedSpool?.asset
                                        ? (chainId === SupportedChains.MAINNET
                                              ? (ASSETS as Record<string, string>)
                                              : (ARBITRUM_ASSETS as Record<
                                                    string,
                                                    string
                                                >))[selectedSpool?.asset.id]
                                        : SupportedAssets.FALLBACK
                                ]
                            }
                            <span>{selectedSpool?.asset.symbol || 'Asset'}</span>
                        </div>
                    </div>
                </div>
                <div className='arrow-swap-container'>
                    <ArrowDownIcon />
                </div>

                <div className='deposit-container deposit-to'>
                    <div className='field-container to'>
                        <span className='field'>To</span>
                        <span>{formatAccount(account || AddressZero)}</span>
                    </div>
                    <div className='field-container balance'>
                        <span className='field'>Balance in Wallet</span>
                        <span>
                            {formatUSD(
                                userBalance
                                    ? formatUnits(
                                          userBalance,
                                          selectedSpool?.asset.decimals
                                      )
                                    : 0
                            )}
                        </span>
                    </div>
                    <div className='field-container input'>
                        <span className='field'>
                            Output
                            {modalData?.type === WithdrawlTypes.SLOW
                                ? null
                                : ' (May incur slippage and fees)'}
                        </span>
                        <div className='input-container'>
                            <TextInput
                                value={withdrawAddresses.from}
                                name='to'
                                placeholder='0.00'
                                readOnly
                            />
                        </div>
                    </div>
                    <div className='field-container token'>
                        <div>
                            {
                                AssetIcons[
                                    selectedSpool?.asset
                                        ? (chainId === SupportedChains.MAINNET
                                              ? (ASSETS as Record<string, string>)
                                              : (ARBITRUM_ASSETS as Record<
                                                    string,
                                                    string
                                                >))[selectedSpool?.asset.id]
                                        : SupportedAssets.FALLBACK
                                ]
                            }
                            <span>{selectedSpool?.asset.symbol || 'Asset'}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className='buttons-container '>
                <Button
                    onClick={withdraw}
                    disabled={
                        !withdrawAddresses.from ||
                        parseUnits(
                            withdrawAddresses.from,
                            selectedSpool?.asset.decimals
                        ).isZero()
                    }
                >
                    Withdraw ({modalData?.type})
                </Button>
            </div>
        </CustomModal>
    );
};

export default SpoolWithdraw;
